<template>
   <b-container class="pt-1 pb-4">
      <div class="standard-width mx-auto">
         <h1 class="page-title">Create {{ userLabel }} Account</h1>
      </div>
      <b-card bg-variant="white" class="standard-width mx-auto">
         <b-form @submit.prevent="onSubmit">
            <!-- Name -->
            <b-form-row>
               <b-col md="6">
                  <b-form-group label="First Name" label-for="input-first-name">
                     <b-form-input
                        id="input-first-name"
                        v-model="profile.firstName"
                        trim
                        :state="profile.firstName ? !$v.profile.firstName.$invalid : null"
                        ref="firstName"
                     ></b-form-input>
                  </b-form-group>
               </b-col>
               <b-col md="6">
                  <b-form-group label="Last Name" label-for="input-last-name">
                     <b-form-input
                        id="input-last-name"
                        v-model="profile.lastName"
                        trim
                        :state="profile.lastName ? !$v.profile.lastName.$invalid : null"
                     ></b-form-input>
                  </b-form-group>
               </b-col>
            </b-form-row>

            <!-- Email -->
            <b-form-group
               label="Email"
               label-for="input-email"
               invalid-feedback="Please enter a valid email"
               description="This will be the user name for logging in."
            >
               <b-form-input
                  id="input-email"
                  v-model="profile.email"
                  trim
                  :state="profile.email ? !$v.profile.email.$invalid : null"
               ></b-form-input>
            </b-form-group>

            <!-- Company -->
            <b-form-group
               label="Company"
               label-for="select-company"
               v-if="isClient && !this.isExternalPartner"
            >
               <b-form-select
                  id="select-company"
                  :options="companyOptions"
                  v-model="companyId"
                  :disabled="null !== company"
                  @input="loadSuggestions"
               ></b-form-select>
            </b-form-group>

            <!-- External Company -->
            <b-form-group
               label="External Company"
               label-for="select-external-company"
               v-show="isClient && isExternalPartner"
            >
               <b-form-input
                  id="select-external-company"
                  type="search"
                  v-model="extPartnerCompany"
                  href="#"
                  tabindex="0"
                  ref="externalCompanyRef"
                  autocomplete="off"
               ></b-form-input>
               <b-popover
                  v-show="externalCompanies.length > 0"
                  target="select-external-company"
                  placement="bottomleft"
                  triggers="click blur"
                  :custom-class="`external-partner-popover ${
                     externalCompanies.length === 0 ? 'external-partner-popover--no-borders' : ''
                  }`"
               >
                  <datalist class="d-flex flex-column external-partner-datalist">
                     <option
                        @click="onExternalCompanySelect(externalCompany)"
                        class="external-company-option text-truncate"
                        v-for="externalCompany in externalCompanies"
                        :key="externalCompany"
                     >
                        {{ externalCompany }}
                     </option>
                  </datalist>
               </b-popover>
            </b-form-group>

            <!-- Job Title -->
            <b-form-group label="Job Title" label-for="input-job-title" v-if="isClient">
               <b-form-input
                  id="input-job-title"
                  list="datalist-titles"
                  autocomplete="off"
                  v-model="profile.title"
                  trim
               ></b-form-input>

               <datalist id="datalist-titles" v-if="companyId">
                  <option v-for="title in companyTitles" :key="title">
                     {{ title }}
                  </option>
               </datalist>
            </b-form-group>

            <!-- Department -->
            <b-form-group label="Department" label-for="input-department" v-if="isClient">
               <b-form-input
                  id="input-department"
                  list="datalist-departments"
                  autocomplete="off"
                  v-model="profile.department"
                  trim
               ></b-form-input>

               <datalist id="datalist-departments" v-if="companyId">
                  <option v-for="department in companyDepartments" :key="department">
                     {{ department }}
                  </option>
               </datalist>
            </b-form-group>

            <!-- Subsidiary -->
            <b-form-group
               label="Subsidiary Company Name"
               label-for="input-subsidiary"
               v-if="isClient && !isExternalPartner"
            >
               <b-form-input
                  id="input-subsidiary"
                  v-model="profile.subsidiary"
                  list="datalist-subsidiaries"
                  autocomplete="off"
               ></b-form-input>

               <datalist id="datalist-subsidiaries" v-if="companyId">
                  <option v-for="subsidiary in companySubsidiaries" :key="subsidiary">
                     {{ subsidiary }}
                  </option>
               </datalist>
            </b-form-group>

            <div v-if="isClient && !isExternalPartner">
               <h5 class="mt-2">Permissions</h5>
               <div class="permission-item">
                  <label>
                     Create employees & view/edit payroll values
                     <input type="checkbox" v-model="permissions.payroll" />
                  </label>
               </div>
               <div class="permission-item">
                  <label>
                     View and edit company revenue values
                     <input type="checkbox" v-model="permissions.revenue" />
                  </label>
               </div>
               <div class="permission-item">
                  <label>
                     Create contractors & view/edit payment values
                     <input type="checkbox" v-model="permissions.contractorPayments" />
                  </label>
               </div>
            </div>

            <b-button type="submit" variant="primary" :disabled="$v.$invalid || submitting"
               >Submit</b-button
            >
         </b-form>
      </b-card>
   </b-container>
</template>

<script>
import {required, requiredIf, email} from 'vuelidate/lib/validators';

import {clientUserSuggestions} from '@/mixins';
import {UserGroups} from '@/store/modules/session';
import {caseTransformer} from '@/helpers/utils';
export default {
   name: 'UserCreate',

   mixins: [
      // Adds support for department/job title suggestions
      clientUserSuggestions,
   ],

   props: {
      userType: {
         type: String,
         default: null,
      },
      company: {
         type: [String, Number],
         default: null,
      },
   },

   async created() {
      let requests = [];

      requests.push(
         this.$store.dispatch('companies/fetchCompanies').then((companies) => {
            this.companies = companies;
         })
      );

      if (this.isExternalPartner) {
         requests.push(
            this.$store.dispatch('companies/fetchExternalCompanies').then((externalCompanies) => {
               this.externalCompanies = externalCompanies;
            })
         );
      }

      if (this.company) {
         requests.push(this.loadSuggestions());
      }

      await this.loadPage(requests);

      if (this.userLabel === 'Customer') {
         this.permissions = {
            payroll: true,
            revenue: true,
            contractorPayments: true,
         };
      }
   },

   data() {
      return {
         profile: {
            firstName: null,
            lastName: null,
            email: null,
            title: null,
            department: null,
            subsidiary: null,
         },
         permissions: {
            payroll: false,
            revenue: false,
            contractorPayments: false,
         },
         companies: null,
         externalCompanies: [],
         extPartnerCompany: null,
         companyId: this.company,
         showExternalCompanies: false,
         submitting: false,
      };
   },

   computed: {
      userLabel() {
         if (!this.userType) {
            return 'User';
         }
         if (UserGroups.SME === this.userType) {
            return this.userType;
         } else if (UserGroups.EXTERNAL_PARTNER === this.userType) {
            return 'External Partner';
         } else {
            return caseTransformer.UCFIRST(this.userType);
         }
      },

      isClient() {
         return [UserGroups.CUSTOMER, UserGroups.SME, UserGroups.EXTERNAL_PARTNER].includes(
            this.userType
         );
      },

      isExternalPartner() {
         return this.userType === UserGroups.EXTERNAL_PARTNER;
      },

      isStaffOrAdmin() {
         return this.$store.getters.isStaff || this.$store.getters.isAdmin;
      },

      companyOptions() {
         if (!this.companies) {
            return [{text: 'Loading...', value: null}];
         } else {
            return [
               {text: 'Please select one', value: null},
               ...this.companies.map((company) => {
                  return {text: company.companyName, value: company.id};
               }),
            ];
         }
      },
   },
   mounted() {
      this.$refs.firstName.$el.focus();
   },
   methods: {
      onExternalCompanySelect(company) {
         this.extPartnerCompany = company;
         this.$refs.externalCompanyRef.$el.click();
      },
      /** Submit a create user request */
      async onSubmit() {
         let user = {
            ...this.profile,
         };

         if (this.userType) {
            user.group = this.userType;
         }

         if (this.companyId && !this.isExternalPartner) {
            user.companyId = this.companyId;
         } else {
            user.companyId = 0;
         }

         if (this.isExternalPartner) {
            user.extPartnerCompany = this.extPartnerCompany;
         }

         try {
            let response;
            const userTypes = [UserGroups.CUSTOMER, UserGroups.SME];

            if (this.isStaffOrAdmin && userTypes.includes(this.userType)) {
               this.submitting = true;
               this.$emit('showPageload', true);

               response = await this.$store.dispatch('users/createUser', {user});
            } else {
               response = await this.blockingRequest('users/createUser', {user});
            }

            if (response?.id) {
               this.$store
                  .dispatch('users/createUserPermissions', {
                     userId: response.id,
                     payroll: this.permissions.payroll,
                     revenue: this.permissions.revenue,
                     contractorPayments: this.permissions.contractorPayments,
                     isInvitation: false,
                  })
                  .catch((e) => console.log(e));
            }

            const companyId = this.companyId || response.companyId;

            if (companyId) {
               await this.$router.push({name: 'company-detail', params: {id: companyId}});
            } else {
               await this.$router.push({name: 'Home'});
            }
         } catch (err) {
            this.$store.commit('showAlert', {
               response: err.response,
               fallbackMsg: 'Submission failed',
               seconds: 5,
            });
         } finally {
            if (this.submitting) {
               this.submitting = false;
               this.$emit('showPageload', false);
            }
         }
      },
   },
   validations: {
      profile: {
         firstName: {
            required,
         },
         lastName: {
            required,
         },
         email: {
            required,
            email,
         },
      },
      companyId: {
         required: requiredIf(function () {
            return this.isClient && !this.isExternalPartner;
         }),
      },
   },
};
</script>

<style scoped lang="scss">
.external-partner-datalist {
   width: 274px;
}
.external-company-option {
   cursor: pointer;
   padding: 0.2rem 0.5rem;
   max-width: 274px;
   &:hover {
      background: $gray-400;
   }
}
</style>
